import {
  leaveSession,
  leaveSessionDueToIdleTimeout,
  useBaseStore,
  useConfigStore,
  useTranslation,
} from '@cometchat/calls-sdk-core';
import clsx from 'clsx';
import React, { useEffect } from 'react';
import './ideal-timeout-modal.css';

export interface IdealTimeoutModalProps {
  className?: string;
}

function getFormattedTime(ms: number): string {
  const minutes = Math.floor(ms / 60000)
    .toString()
    .padStart(2, '0');
  const seconds = Math.floor((ms % 60000) / 1000)
    .toString()
    .padStart(2, '0');
  return `${minutes}:${seconds}`;
}

const IdealTimeoutModal: React.FC<IdealTimeoutModalProps> = ({
  className = '',
}) => {
  const idleTimeoutModalVisible = useBaseStore(
    state => state.idleTimeoutModalVisible,
  );
  const idleTimeoutPeriodAfterPrompt = useConfigStore(
    state => state.idleTimeoutPeriodAfterPrompt,
  );
  const [ms, setMs] = React.useState(idleTimeoutPeriodAfterPrompt);
  const formattedTime = getFormattedTime(ms);
  const ceilMinutes = Math.ceil(ms / 60000);
  const { t } = useTranslation();

  const onStayInCall = () => {
    useBaseStore.getState().restartIdealTimeoutTimer();
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onStayInCall();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onStayInCall();
    }
  };

  useEffect(() => {
    const timer = setInterval(() => {
      console.log('setInterval is running for IdealTimeoutModal');
      if (!idleTimeoutModalVisible) {
        clearInterval(timer);
        return;
      }
      setMs(prev => {
        if (prev <= 1000) {
          clearInterval(timer);
          leaveSessionDueToIdleTimeout();
          return 0;
        }
        return prev - 1000;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [idleTimeoutModalVisible]);

  useEffect(() => {
    if (idleTimeoutModalVisible) {
      setMs(idleTimeoutPeriodAfterPrompt);
    }
  }, [idleTimeoutModalVisible, idleTimeoutPeriodAfterPrompt]);

  if (!idleTimeoutModalVisible) {
    return null;
  }

  return (
    <div
      className="cometchat-calls-ideal-timeout-modal-overlay"
      onClick={handleOverlayClick}
      role="presentation"
      aria-hidden="false">
      <div
        className={clsx('cometchat-calls-ideal-timeout-modal', className)}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ideal-timeout-title"
        aria-describedby="ideal-timeout-subtitle"
        onKeyDown={handleKeyDown}
        tabIndex={-1}>
        <div className="cometchat-calls-ideal-timeout-modal__content">
          <div className="cometchat-calls-ideal-timeout-modal__timer-icon">
            <span className="cometchat-calls-ideal-timeout-modal__timer-text">
              {formattedTime}
            </span>
          </div>

          <div className="cometchat-calls-ideal-timeout-modal__text-content">
            <h2
              id="ideal-timeout-title"
              className="cometchat-calls-ideal-timeout-modal__title">
              {t('idealTimeout.title')}
            </h2>
            <p
              id="ideal-timeout-subtitle"
              className="cometchat-calls-ideal-timeout-modal__subtitle">
              {t('idealTimeout.subtitle')
                .replace('{minutes}', ceilMinutes.toString())
                .replace('{plural}', ceilMinutes > 1 ? 's' : '')}
            </p>
          </div>

          <div className="cometchat-calls-ideal-timeout-modal__actions">
            <button
              className="cometchat-calls-ideal-timeout-modal__button cometchat-calls-ideal-timeout-modal__button--secondary"
              onClick={onStayInCall}
              type="button"
              aria-label={t('idealTimeout.buttons.stayInCall')}>
              {t('idealTimeout.buttons.stayInCall')}
            </button>
            <button
              className="cometchat-calls-ideal-timeout-modal__button cometchat-calls-ideal-timeout-modal__button--primary"
              onClick={() => leaveSession()}
              type="button"
              aria-label={t('idealTimeout.buttons.leaveNow')}
              autoFocus>
              {t('idealTimeout.buttons.leaveNow')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IdealTimeoutModal;
